import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";

const LINKS = [
  { label: "Home", id: "home" },
  { label: "What We Do", id: "what-we-do" },
  { label: "Solutions", id: "solutions" },
  { label: "How It Works", id: "how-it-works" },
  { label: "Industries", id: "industries" },
  { label: "About", id: "about" },
  { label: "Contact", id: "contact" },
];

export const useScrollTo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  return (id) => {
    const scroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(scroll, 120);
    } else {
      scroll();
    }
  };
};

const Wordmark = ({ light = false }) => (
  <span className="leading-none" data-testid="brand-wordmark">
    <span className={`block font-heading font-extrabold tracking-[0.22em] text-[15px] ${light ? "text-white" : "text-charcoal"}`}>
      WESTLINE
    </span>
    <span className={`block font-heading font-semibold tracking-[0.26em] text-[10px] mt-[3px] ${light ? "text-sage-light" : "text-forest"}`}>
      TECHNOLOGIES
    </span>
  </span>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const scrollTo = useScrollTo();

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <header className="sticky top-0 z-50 bg-offwhite/85 backdrop-blur-xl border-b border-gray-200" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-6">
        <button onClick={() => go("home")} className="shrink-0 text-left" data-testid="nav-logo-button" aria-label="Westline Technologies home">
          <Wordmark />
        </button>
        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {LINKS.slice(1).map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="text-[13px] font-medium text-zinc-600 hover:text-forest transition-colors duration-200"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <button
            onClick={() => go("contact")}
            className="hidden sm:inline-flex items-center gap-2 bg-forest text-white text-[13px] font-semibold px-5 py-2.5 rounded-md hover:bg-forest-mid transition-colors duration-200"
            data-testid="book-discovery-call-button"
          >
            Book a Discovery Call
            <ArrowRight size={14} />
          </button>
          <button
            className="lg:hidden text-charcoal p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-gray-200 bg-offwhite px-5 py-4 space-y-1" data-testid="mobile-menu">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="block w-full text-left py-2.5 text-sm font-medium text-zinc-700 hover:text-forest"
              data-testid={`mobile-nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("contact")}
            className="mt-2 w-full inline-flex justify-center items-center gap-2 bg-forest text-white text-sm font-semibold px-5 py-3 rounded-md"
            data-testid="mobile-book-discovery-call-button"
          >
            Book a Discovery Call
            <ArrowRight size={14} />
          </button>
        </div>
      )}
    </header>
  );
};

export { Wordmark };
